<script lang="ts">
	import '../../app.css';
	import favicon from '$lib/assets/favicon.svg';
	import Header from '$lib/components/Header.svelte';
	import Footer from '$lib/components/Footer.svelte';
	import { Terminal } from '$lib/terminal';
	import PageTransition from '$lib/components/PageTransition.svelte';
	import CursorGlow from '$lib/components/CursorGlow.svelte';
	import CookieConsent from '$lib/components/CookieConsent.svelte';
	import { onMount, setContext } from 'svelte';
	import { isMobileDevice } from '$lib/utils/motion';
	import { afterNavigate } from '$app/navigation';
	import { browser } from '$app/environment';
	import { page } from '$app/stores';
	import { initPostHog, capturePageview, captureEvent } from '$lib/analytics/posthog';
	import { initWebVitals } from '$lib/analytics/web-vitals';
	import { t, type Language } from '$lib/i18n';
	import { useTheme } from '$lib/stores/theme.svelte';
	import { createKeyboardHandler } from '$lib/utils/keyboard';
	import CommandPalette from '$lib/components/CommandPalette.svelte';
	import BackToTop from '$lib/components/BackToTop.svelte';
	import ScrollProgress from '$lib/components/ScrollProgress.svelte';
	import SkeletonLoader from '$lib/components/SkeletonLoader.svelte';

	let { children, data } = $props();
	let lang = $derived(data.lang as Language);
	let tr = $derived(t(lang));

	let showTerminal = $state(false);
	let showPalette = $state(false);
	let terminalSource = $state('shortcut');

	const theme = useTheme();
	setContext('theme', theme);

	onMount(() => {
		// Apply saved theme
		theme.init();

		// Initialize PostHog if consent given
		initPostHog();
		initWebVitals();

		// Listen for consent changes (type-safe via CustomEventMap in app.d.ts)
		const handleConsent = (e: CustomEvent<'accepted' | 'declined'>) => {
			if (e.detail === 'accepted') {
				initPostHog(); // Also captures initial pageview
				initWebVitals();
			}
		};
		window.addEventListener('cookie-consent', handleConsent);

		const handleKeydown = createKeyboardHandler({
			onTogglePalette: () => {
				showPalette = !showPalette;
				if (showPalette) captureEvent('command_palette_opened');
			},
			onToggleTerminal: () => {
				if (isMobileDevice()) return; // Disable terminal on mobile for performance
				if (!showTerminal) terminalSource = 'shortcut';
				showTerminal = !showTerminal;
			},
			onSecretCode: () => {
				if (isMobileDevice()) return; // Disable terminal on mobile for performance
				terminalSource = 'konami';
				showTerminal = true;
			}
		});
		window.addEventListener('keydown', handleKeydown);

		return () => {
			window.removeEventListener('keydown', handleKeydown);
			window.removeEventListener('cookie-consent', handleConsent);
		};
	});

	// Set document lang attribute dynamically
	$effect(() => {
		if (browser) {
			document.documentElement.lang = lang;
		}
	});

	// Track page views on client-side navigation only
	afterNavigate(() => {
		capturePageview(window.location.href);
	});

	let baseUrl = $derived($page.url.origin);
	let ogImage = $derived(`${baseUrl}/og-image.png`);

	// Get current path without language prefix for alternate URLs
	let currentPath = $derived($page.url.pathname.replace(/^\/(en|no)/, '') || '/');
	let ogLocale = $derived(lang === 'no' ? 'nb_NO' : 'en_US');
	let siteTitle = $derived(
		lang === 'en' ? 'Ameta | Industrial Data Platform' : 'Ameta | Industriell dataplattform'
	);
	let siteDesc = $derived(tr('hero_subtitle'));
</script>

<svelte:head>
	<link rel="icon" href={favicon} />
	<link rel="preconnect" href="https://fonts.googleapis.com" />
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
	<link
		href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
		rel="stylesheet"
	/>

	<title>{siteTitle}</title>
	<meta name="description" content={siteDesc} />

	<!-- Open Graph -->
	<meta property="og:type" content="website" />
	<meta property="og:site_name" content="Ameta" />
	<meta property="og:title" content={siteTitle} />
	<meta property="og:description" content={siteDesc} />
	<meta property="og:image" content={ogImage} />
	<meta property="og:url" content="{baseUrl}/{lang}{currentPath}" />
	<meta property="og:locale" content={ogLocale} />

	<!-- Twitter -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={siteTitle} />
	<meta name="twitter:description" content={siteDesc} />
	<meta name="twitter:image" content={ogImage} />

	<!-- Language alternates -->
	<link rel="alternate" hreflang="en" href="{baseUrl}/en{currentPath}" />
	<link rel="alternate" hreflang="no" href="{baseUrl}/no{currentPath}" />
	<link rel="alternate" hreflang="x-default" href="{baseUrl}/en{currentPath}" />
</svelte:head>

<ScrollProgress />
<SkeletonLoader />
<div class="min-h-screen flex flex-col aurora-bg" inert={showTerminal || showPalette}>
	{#if !isMobileDevice()}<CursorGlow />{/if}
	<Header {lang} />
	<main id="main-content" class="flex-1 relative z-10">
		<svelte:boundary>
			<PageTransition>
				{@render children()}
			</PageTransition>
			{#snippet failed(_error, reset)}
				<div class="flex-1 flex items-center justify-center p-8 text-center">
					<div>
						<p class="text-red-400 mb-4">Something went wrong loading this page.</p>
						<button
							onclick={reset}
							class="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition">Try again</button
						>
					</div>
				</div>
			{/snippet}
		</svelte:boundary>
	</main>
	<Footer {lang} />
</div>

<BackToTop />
<CookieConsent {lang} />

{#if showTerminal}
	<svelte:boundary>
		<Terminal {lang} source={terminalSource} onClose={() => (showTerminal = false)} />
		{#snippet failed(_error, reset)}
			<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
				<div class="text-center p-8">
					<p class="text-red-400 mb-4">Terminal failed to load.</p>
					<button
						onclick={() => {
							showTerminal = false;
						}}
						class="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition mr-2">Close</button
					>
					<button onclick={reset} class="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition"
						>Retry</button
					>
				</div>
			</div>
		{/snippet}
	</svelte:boundary>
{/if}

{#if showPalette}
	<CommandPalette
		{lang}
		onClose={() => (showPalette = false)}
		onOpenTerminal={() => {
			if (isMobileDevice()) return; // Disable terminal on mobile for performance
			showPalette = false;
			terminalSource = 'palette';
			showTerminal = true;
		}}
	/>
{/if}
